import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { fetchBanners, fetchFolders, fetchLessons, fetchStudents } from "./actions";
import { StudentType } from "../types/User";
import { FolderType } from "../types/CounselorData";
import { LessonType } from "../types/Lesson";
import UserContext from "./user-context";
import { BannerServerResponseType } from "../types/Banner";
import { RoomResponseType } from "../types/Chat";

const GeneralDataContext = createContext({
    lessons: [] as LessonType[],
    banners: [] as BannerServerResponseType,
    folders: null as FolderType[] | null | undefined,
    students: null as StudentType[] | null | undefined,
    chatrooms: null as RoomResponseType | null | undefined,

    retryFetchFolders: () => {},
    retryFetchStudents: () => {},
    addFolder: (newFolder: FolderType) => {},
    updateStudents: (newValues: StudentType[]) => {},
    setChatrooms: (chatrooms: RoomResponseType | null) => {}
})

type GeneralDataContextProviderProps = {
    children: ReactNode;
}

export function GeneralDataContextProvider({ children }: GeneralDataContextProviderProps) {
    const userContext = useContext(UserContext)
    const user = userContext?.user;

    const [lessons, setLessons] = useState<LessonType[]>([]);
    const [banners, setBanners] = useState<BannerServerResponseType>([]);
    const [chatrooms, setChatrooms] = useState<RoomResponseType | null>(null);
    const [folders, setFolders] = useState<FolderType[] | null | undefined>(null);
    const [students, setStudents] = useState<StudentType[] | null | undefined>(null);

    function addFolder(newFolder: FolderType) {
        setFolders(prev => {
            const newArray = [...(prev || [])];
            newArray.push(newFolder);
            return newArray;
        })
    }

    function updateStudents(newValues: StudentType[]) {
        setStudents(newValues);
    }

    async function retryFetchStudents() {
        if (user && user.role == 1) {
            setStudents(null);
            await fetchStudents(user.special.id, setStudents);
        }
    }

    async function retryFetchFolders() {
        if (user && user.role == 1) {
            setFolders(null);
            await fetchFolders(user.special.id, setFolders);
        }
    }

    const context = {
        folders,
        lessons,
        banners,
        students,
        chatrooms,
        

        addFolder,
        setChatrooms,
        updateStudents,
        retryFetchFolders,
        retryFetchStudents,
    }

    useEffect(() => {
        async function initial() {
            const location = window.location.pathname;
            if (location.includes('login') || location.includes('register') || location.includes('demo') || location.includes('payment'))
                return;

            if (user && user.role == 1) {
                await fetchStudents(user.special.id, setStudents); 
                await fetchLessons(setLessons);
                await fetchFolders(user.special.id, setFolders);
                await fetchBanners(setBanners);
            } else if (user && user.role == 2) {
                await fetchLessons(setLessons);
                await fetchBanners(setBanners);
                setStudents([]);
                setFolders([]);
            }
        }
        
        initial();
    }, [user])

    return (
        <GeneralDataContext.Provider value={context}>
            {children}
        </GeneralDataContext.Provider>
    )
}

export default GeneralDataContext;
